import React, { useEffect, createContext } from "react";

import ModalBody from "./ModalBody";
import ModalFooter from "./ModalFooter";
import ModalHeader from "./ModalHeader";

/**
 * Internally uses Bulma classes in Modal Card https://bulma.io/documentation/components/modal/
 *
 * @example
 *
 * import Modal from "~/components/Modal";
 *
 * <Modal active onClose={()=> handle close in your component}>
 *  <Modal.Header>Your title here</Modal.Header>
 *  <Modal.Body>Your content here</Modal.Body>
 *  <Modal.Footer>Your footer here</Modal.Footer>
 * </Modal>
 */

type ModalProps = {
  active: boolean;
  children: React.ReactNode;
  onClose: () => void;
  styles?: React.CSSProperties;
  overflow?: string;
};

const ModalContext = createContext<Partial<ModalProps>>({});

export function useModalContext() {
  const context = React.useContext(ModalContext);
  if (!context.onClose) {
    throw new Error(`Modal compound components cannot be rendered outside the Modal component`);
  }
  return context;
}

export default function Modal({ active, onClose, children, styles, overflow = "scroll" }: ModalProps) {
  useEffect(() => {
    // this prevents scrolling on the containing app
    document.querySelector("html")?.classList.toggle("is-clipped", active);
    return function cleanup() {
      document.querySelector("html")?.classList.toggle("is-clipped", false);
    };
  }, [active]);

  if (!active) {
    return null;
  }

  return (
    <ModalContext.Provider value={{ onClose }}>
      <div className={"modal is-active"}>
        <div className="modal-background" data-testid="modal-background" onClick={onClose}></div>
        <div style={{ minWidth: "500px", borderRadius: "4px", ...styles }} className="modal-card">
          <div style={{ overflow: overflow }}>{children}</div>
        </div>
      </div>
    </ModalContext.Provider>
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
